/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {BlueskyIcon} from '@app/components/icons/BlueskyIcon';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import type {ScrollerHandle} from '@app/components/uikit/Scroller';
import {WHATS_NEW_ENTRIES, type WhatsNewEntry} from '@app/components/whats_new/WhatsNewEntries';
import styles from '@app/components/whats_new/WhatsNewModal.module.css';
import {SafeMarkdown} from '@app/lib/markdown';
import {MarkdownContext} from '@app/lib/markdown/renderers/RendererTypes';
import WhatsNewStore from '@app/stores/WhatsNewStore';
import markupStyles from '@app/styles/Markup.module.css';
import {ExternalUrls} from '@fluxer/constants/src/ExternalUrls';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon} from '@phosphor-icons/react';
import {useCallback, useEffect, useRef, useState} from 'react';

const WHATS_NEW_MODAL_KEY = 'whats-new';

export function openWhatsNewModal(): void {
	const latestEntry = WHATS_NEW_ENTRIES[0];
	if (!latestEntry) return;
	ModalActionCreators.pushWithKey(
		modal(() => <WhatsNewModal entry={latestEntry} />),
		WHATS_NEW_MODAL_KEY,
	);
}

interface WhatsNewModalProps {
	entry: WhatsNewEntry;
}

function formatEntryDate(date: Date): string {
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export function WhatsNewModal({entry}: WhatsNewModalProps) {
	const {t} = useLingui();
	const initialFocusRef = useRef<HTMLDivElement | null>(null);
	const scrollerRef = useRef<ScrollerHandle | null>(null);
	const [isScrollHintVisible, setIsScrollHintVisible] = useState(false);

	const handleDismiss = useCallback(() => {
		WhatsNewStore.dismiss(entry.id);
		ModalActionCreators.pop();
	}, [entry.id]);

	const updateScrollHintVisibility = useCallback(() => {
		const scrollerNode = scrollerRef.current?.getScrollerNode();
		if (!scrollerNode) {
			setIsScrollHintVisible(false);
			return;
		}

		const maxScroll = Math.max(0, scrollerNode.scrollHeight - scrollerNode.clientHeight);
		const distanceFromBottom = maxScroll - scrollerNode.scrollTop;
		const shouldShowScrollHint = maxScroll > 16 && scrollerNode.scrollTop < 72 && distanceFromBottom > 24;
		setIsScrollHintVisible(shouldShowScrollHint);
	}, []);

	const handleContentScroll = useCallback(() => {
		updateScrollHintVisibility();
	}, [updateScrollHintVisibility]);

	const handleContentResize = useCallback(() => {
		updateScrollHintVisibility();
	}, [updateScrollHintVisibility]);

	const handleScrollToBottom = useCallback(() => {
		scrollerRef.current?.scrollToBottom({animate: true});
	}, []);

	useEffect(() => {
		updateScrollHintVisibility();
	}, [entry.id, updateScrollHintVisibility]);

	return (
		<Modal.Root
			size="medium"
			className={styles.whatsNewRoot}
			centered
			onClose={handleDismiss}
			initialFocusRef={initialFocusRef}
		>
			<Modal.ScreenReaderLabel text={t`What's New`} />
			<div className={styles.contentShell}>
				<Modal.Content
					padding="none"
					ref={scrollerRef}
					fade={false}
					onScroll={handleContentScroll}
					onResize={handleContentResize}
				>
					<div className={styles.contentBody} ref={initialFocusRef} tabIndex={-1}>
						<img src={entry.coverImage} alt="" width={1920} height={1080} className={styles.coverImage} />
						<div className={styles.dateLine}>{formatEntryDate(entry.date)}</div>
						<div className={`${markupStyles.markup} ${styles.markdownBody}`}>
							<SafeMarkdown content={entry.content} options={{context: MarkdownContext.STANDARD_WITHOUT_JUMBO}} />
						</div>
					</div>
				</Modal.Content>
				<button
					type="button"
					className={isScrollHintVisible ? `${styles.scrollHint} ${styles.scrollHintVisible}` : styles.scrollHint}
					aria-hidden={!isScrollHintVisible}
					tabIndex={isScrollHintVisible ? 0 : -1}
					onClick={handleScrollToBottom}
				>
					<div className={styles.scrollHintCircle}>
						<CaretDownIcon size={16} weight="bold" />
					</div>
				</button>
			</div>
			<Modal.Footer className={styles.footer}>
				<a href={ExternalUrls.BLUESKY} target="_blank" rel="noopener noreferrer" className={styles.blueskyLink}>
					<BlueskyIcon size={16} className={styles.blueskyIcon} />
					{t`Follow us on Bluesky`}
				</a>
				<Button variant="primary" fitContent className={styles.dismissButton} onClick={handleDismiss}>
					{t`Got it!`}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
}
